import {
  Brand,
  Optional,
  PLATFORM_ENTITY_REF,
} from '@agilelab/plugin-wb-platform-common';
import { EventsServiceSubscribeOptions } from '@backstage/plugin-events-node';
import { v4 as uuid } from 'uuid';
import {
  CreateEventMetadata,
  EventParams,
  EventSchema,
  WbEventParams,
} from './types';

export type TopicId = Brand<string, 'TopicId'>;

/**
 * do not export
 * @see createTopic
 */
function createTopicIdFromString(namespace: string, name: string): TopicId {
  const parsedNamespace = namespace.toLocaleLowerCase().trim();
  const parsedName = name.toLocaleLowerCase().trim();

  // Regular expression pattern to match lowercase letters, numbers, hyphens, and dots
  const pattern = /^[a-z0-9-]+$/;

  if (!pattern.test(parsedNamespace)) {
    throw new Error(
      `A topic namespace must only contain lowercase letters, numbers and hyphens. Invalid topic namespace=${namespace}`,
    );
  }

  // Test if the topicId matches the pattern
  if (!pattern.test(parsedName)) {
    throw new Error(
      `A topic name must only contain lowercase letters, numbers and hyphens. Invalid topic name=${name}`,
    );
  }

  return `${parsedNamespace}.${parsedName}` as TopicId;
}

export type EventPayload = any;

/**
 * Event metadata that is common to all events types.
 *
 * @remarks correlationId is auto-generated if not provided.
 * @remarks publishedAt is auto-generated if not provided.
 * @remarks originatorUri is auto-generated if not provided
 */
export type EventMetadataOverride = Optional<
  CreateEventMetadata,
  'correlationId' | 'publishedAt' | 'originatorUri'
>;

export type TopicInfo = {
  id: TopicId;
  namespace: string;
  name: string;
  description: string;
};

export type EventSubscriber = EventsServiceSubscribeOptions;

export type Topic<T extends EventPayload = EventPayload> = {
  typeof: 'topic';

  /**
   * Create an event that is compatible with the schema
   * This gives type inference when creating an event for this topic
   * @param payload
   * @param metadata
   * @returns
   */
  createEvent: (payload: T, metadata: EventMetadataOverride) => EventParams<T>;
  createSubscriber: (
    onEvent: (params: WbEventParams<T>) => Promise<void>,
  ) => EventSubscriber;
  topic: TopicInfo;
  schema: EventSchema<T>;
};

/**
 * A facility to create topics and bind them to a schema.
 *
 * This is due to the fact that each event type can be published only to its specific topic. Thus, 1 topic = 1 event type = 1 event schema.
 * This helper function will help you create a topic that can be registered into the SchemaRegistry.
 * It also gives you an helper function to create events that are compatible to the schema.
 *
 * @example
 * ```ts
 * // suppose you want to define a new event called SnapshotCreatedEvent
 * // let's create an event schema for it using zod
 * const snapshotCreatedEventSchema = z.object({
 *   snapshotId: z.string(),
 *   snapshotName: z.string(),
 * });
 *
 * // and it's inferred type
 * type SnapshotCreatedEvent = z.infer<typeof snapshotCreatedEventSchema>
 *
 * // now let's create a topic for it
 * const snapshotCreatedTopic = createTopic<SnapshotCreatedEvent>({
 *   namespace: 'builder', // a namespace is a logical grouping of topics
 *   name: 'snapshot-created', // a name is the name of the topic itself (remember 1 topic = 1 event type, thus, for witboost, the name of the topic is the event name itself)
 *   description: 'A snapshot has been created',
 * }, snapshotCreatedEventSchema);
 *
 * // now we can publish an event to the topic by using the helper function createEvent
 * eventBroker.publish(snapshotCreatedTopic.createEvent({
 *   payload: {
 *     snapshotId: uuid(),
 *     snapshotName: 'my-snapshot',
 *   },
 *   metadata: {
 *     // ...event metadata
 *   }
 * }));
 * ```
 *
 * @param topicInfo - a name, namespace and a description for the topic. the topic ID is the combination of namespace.name
 * @param schema - a Zod schema definition
 * @returns a topic that can be registered into the registry (@see SchemaRegistry)
 */
export function createTopic<T extends EventPayload = EventPayload>(
  topicInfo: {
    namespace: string;
    name: string;
    description: string;
  },
  schema: EventSchema<T>,
): Topic<T> {
  const topicId = createTopicIdFromString(topicInfo.namespace, topicInfo.name);

  return {
    typeof: 'topic',
    topic: {
      id: topicId,
      namespace: topicInfo.namespace,
      name: topicInfo.name,
      description: topicInfo.description,
    },
    createEvent: (payload: T, metadata: EventMetadataOverride) => ({
      topic: topicId,
      eventPayload: payload,
      metadata: {
        publishedAt: metadata.publishedAt ?? new Date().toISOString(),
        correlationId: metadata.correlationId ?? uuid(),
        generatorSystem: metadata.generatorSystem,
        originatorUri:
          metadata.originatorUri?.replace(':default/', ':') ??
          PLATFORM_ENTITY_REF,
        keyBuilderCatalog:
          metadata.keyBuilderCatalog?.replace(':default/', ':') ?? undefined,
        keyCgp: metadata.keyCgp ?? undefined,
        keyCoordinator:
          metadata.keyCoordinator?.replace(':default/', ':') ?? undefined,
        keyMarketplace:
          metadata.keyMarketplace?.replace(':default/', ':') ?? undefined,
      },
    }),
    createSubscriber: (
      onEvent: (params: WbEventParams<T>) => Promise<void>,
    ): EventSubscriber => ({
      id: topicId,
      topics: [topicId],
      onEvent: async params => onEvent(params as WbEventParams<T>),
    }),
    schema,
  };
}
